/**
 * Header: Alpine `headerRoot` (search/cart, drawer scroll lock + scrollbar gap) and `<header-section>`.
 * Load before alpine.js in theme.liquid so `alpine:init` runs after the listener below is registered.
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('headerRoot', () => ({
    cartOpen: false,
    searchOpen: false,
    searchTerm: '',
    overlayOpener: null,

    /**
     * Remembers what opened the search or cart overlay so closing it can hand focus back.
     * @param {Element|null} opener - Usually the clicked trigger, or the focused element when
     *   the cart is opened by a `cart-open` event (e.g. an add-to-cart button)
     */
    rememberOpener(opener) {
      if (!(opener instanceof HTMLElement) || opener === document.body) return;
      this.overlayOpener = opener;
    },

    /**
     * Closes the search and cart overlays and returns focus to whatever opened them, so a
     * keyboard user is not left on content that has just been hidden.
     */
    closeOverlays() {
      if (!this.searchOpen && !this.cartOpen) return;
      this.searchOpen = false;
      this.cartOpen = false;

      const opener = this.overlayOpener;
      this.overlayOpener = null;
      if (!opener?.isConnected) return;
      opener.focus();
    },

    /**
     * Keeps Tab and Shift+Tab cycling inside an open overlay so focus cannot reach the page
     * underneath it. Bind with `@keydown.tab="trapFocus($event)"` on the overlay root.
     * @param {KeyboardEvent} event
     */
    trapFocus(event) {
      const focusable = [...event.currentTarget.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (el) => el.getClientRects().length > 0
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    init() {
      this.searchTerm = this.$el.dataset.initialSearchTerms || '';

      const mq = window.matchMedia('(min-width: 769px)');
      let searchDrawerSb = null;

      const syncSearchDrawerBody = () => {
        const on = mq.matches && this.searchOpen;

        if (!on) {
          document.body.classList.remove('search-drawer-open');
          document.documentElement.style.removeProperty('--search-drawer-scrollbar-gap');
          searchDrawerSb = null;
          return;
        }

        if (searchDrawerSb === null) {
          searchDrawerSb = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        }

        document.body.classList.add('search-drawer-open');

        if (searchDrawerSb) {
          document.documentElement.style.setProperty('--search-drawer-scrollbar-gap', `${searchDrawerSb}px`);
        } else {
          document.documentElement.style.removeProperty('--search-drawer-scrollbar-gap');
        }
      };

      this.$watch('searchOpen', syncSearchDrawerBody);
      mq.addEventListener('change', syncSearchDrawerBody);
      syncSearchDrawerBody();
    },
  }));
});

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Picks the plural form of the cart label for the storefront's locale.
 * @param {HTMLElement} bubble - Cart link/button carrying the `data-cart-aria-template*` attributes
 * @param {number} count - Items in the cart
 * @returns {string|null} Label to apply, or null when the markup has no template
 */
function cartAriaLabel(bubble, count) {
  const other = bubble.getAttribute('data-cart-aria-template');
  if (!other) return null;

  let category = count === 1 ? 'one' : 'other';
  try {
    category = new Intl.PluralRules(document.documentElement.lang || 'en').select(count);
  } catch {
    // An unrecognised lang tag keeps the English rule above.
  }
  // `other` is the unsuffixed attribute; any category the locale does not define falls back to it.
  const template = (category !== 'other' && bubble.getAttribute(`data-cart-aria-template-${category}`)) || other;
  return template.replace(/__COUNT__/g, String(count));
}

function syncCartBubbleAriaLabel(bubble) {
  const countEl = bubble.querySelector('[data-cart-count]');
  const raw = countEl ? countEl.textContent : '0';
  const count = parseInt(raw, 10);
  const label = cartAriaLabel(bubble, Number.isFinite(count) ? count : 0);
  if (!label) return;
  bubble.setAttribute('aria-label', label);
}

export class HeaderSection extends HTMLElement {
  connectedCallback() {
    this.sectionRoot = this.closest('[id^="shopify-section"]');
    if (!this.sectionRoot) return;

    this.lastScrollTop = 0;
    this.onResize = () => this.updateHeaderHeight();
    this.onScroll = () => this.handleScroll();
    this.onCartRequestEnd = (event) => this.handleCartRequestEnd(event);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);

    this.updateHeaderHeight();
    this.handleScroll();

    const bubble = document.getElementById('header-cart-bubble');
    if (bubble) {
      syncCartBubbleAriaLabel(bubble);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  updateHeaderHeight() {
    if (!this.sectionRoot) return;
    const headerHeight = this.sectionRoot.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }

  handleScroll() {
    if (!this.sectionRoot) return;
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

    if (currentScroll > 0) {
      this.sectionRoot.classList.add('scrolled-header');
    } else {
      this.sectionRoot.classList.remove('scrolled-header');
    }

    if (currentScroll > this.lastScrollTop) {
      this.sectionRoot.classList.add('header-sticky');
    } else if (currentScroll < this.lastScrollTop) {
      this.sectionRoot.classList.remove('header-sticky');
    }

    if (currentScroll === 0) {
      this.sectionRoot.classList.remove('header-sticky');
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
  }

  handleCartRequestEnd(event) {
    const cart = event.detail?.cart;
    if (!cart || typeof cart.item_count !== 'number') return;
    const bubble = document.getElementById('header-cart-bubble');
    if (!bubble) return;
    const label = cartAriaLabel(bubble, cart.item_count);
    if (!label) return;
    bubble.setAttribute('aria-label', label);
  }
}

if (!customElements.get('header-section')) {
  customElements.define('header-section', HeaderSection);
}

function syncViewportHeight() {
  const h = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty('--viewport-height', `${h}px`);
}

syncViewportHeight();
window.addEventListener('resize', syncViewportHeight);
window.visualViewport?.addEventListener('resize', syncViewportHeight);
window.visualViewport?.addEventListener('scroll', syncViewportHeight);
